import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import {DockContext, type DockContextValue} from './DockContext';
import {calculateHoverScale, clamp, isPointInsideRect} from './math';
import {
  DEFAULT_DOCK_CONFIG,
  type DockConfig,
  type DockDeleteDetail,
  type DockItemHandle,
  type DockSortDetail,
} from './types';

/** Pointer travel (px) before a press becomes a drag. */
const DRAG_START_DISTANCE = 4;
/** The floating preview sits slightly larger than the icon it replaces. */
const DRAG_SCALE = 1.12;
const REORDER_DURATION = 220;
const REORDER_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const SNAP_BACK_EASE = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

export interface DockProps extends Partial<DockConfig> {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Fires live while dragging, every time the insertion index changes. */
  onSort?: (detail: DockSortDetail) => void;
  /** Fires when an item is dropped outside the dock and `allowDragDelete` is on. */
  onDelete?: (detail: DockDeleteDetail) => void;
}

interface DockBlock {
  ids: string[];
  items: HTMLElement[];
  max: number;
  min: number;
}

interface DragState {
  active: boolean;
  block: DockBlock;
  currentIndex: number;
  el: HTMLElement;
  fromIndex: number;
  id: string;
  offset: {fx: number; fy: number};
  outside: boolean;
  pointerId: number;
  preview: HTMLElement | null;
  previewSize: {height: number; width: number};
  startX: number;
  startY: number;
  translate: {x: number; y: number};
}

/** Move `id` out of the array and reinsert it at `toIndex` of the reduced array. */
export function moveItem(ids: string[], id: string, toIndex: number): string[] {
  const next = ids.filter((entry) => entry !== id);
  next.splice(clamp(toIndex, 0, next.length), 0, id);
  return next;
}

/**
 * The interaction controller.
 *
 * Everything expensive happens on a requestAnimationFrame boundary and follows a
 * strict READ-THEN-WRITE order: every `getBoundingClientRect()` for a frame is
 * taken before the first style write, so the browser never has to flush layout
 * in the middle of the loop (no layout thrashing).
 */
export function Dock({
  children,
  className,
  style,
  onSort,
  onDelete,
  size = DEFAULT_DOCK_CONFIG.size,
  gap = DEFAULT_DOCK_CONFIG.gap,
  padding = DEFAULT_DOCK_CONFIG.padding,
  maxScale = DEFAULT_DOCK_CONFIG.maxScale,
  maxRange = DEFAULT_DOCK_CONFIG.maxRange,
  disabled = DEFAULT_DOCK_CONFIG.disabled,
  direction = DEFAULT_DOCK_CONFIG.direction,
  position = DEFAULT_DOCK_CONFIG.position,
  willChange = DEFAULT_DOCK_CONFIG.willChange,
  easing = DEFAULT_DOCK_CONFIG.easing,
  sortable = DEFAULT_DOCK_CONFIG.sortable,
  allowDragDelete = DEFAULT_DOCK_CONFIG.allowDragDelete,
}: DockProps) {
  const config = useMemo<DockConfig>(
    () => ({
      allowDragDelete,
      direction,
      disabled,
      easing,
      gap,
      maxRange,
      maxScale,
      padding,
      position,
      size,
      sortable,
      willChange,
    }),
    [
      allowDragDelete,
      direction,
      disabled,
      easing,
      gap,
      maxRange,
      maxScale,
      padding,
      position,
      size,
      sortable,
      willChange,
    ],
  );

  const containerRef = useRef<HTMLUListElement>(null);
  const handlesRef = useRef(new Map<string, DockItemHandle>());
  const cfgRef = useRef(config);
  cfgRef.current = config;
  const callbacksRef = useRef({onDelete, onSort});
  callbacksRef.current = {onDelete, onSort};

  const mousePosRef = useRef({x: 0, y: 0});
  const hoverFrameRef = useRef<number | null>(null);
  const pendingHoverRef = useRef<{x: number; y: number} | null>(null);
  const dragFrameRef = useRef<number | null>(null);
  const pendingDragRef = useRef<{x: number; y: number} | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const suppressClickRef = useRef(false);
  const pendingFlipRef = useRef<Map<HTMLElement, DOMRect> | null>(null);

  const register = useCallback((id: string, handle: DockItemHandle) => {
    handlesRef.current.set(id, handle);
    return () => {
      if (handlesRef.current.get(id) === handle) handlesRef.current.delete(id);
    };
  }, []);

  /* ------------------------------------------------------------ DOM helpers */

  const layoutChildren = useCallback((): HTMLElement[] => {
    const ul = containerRef.current;
    if (!ul) return [];
    return (Array.from(ul.children) as HTMLElement[]).filter(
      (el) => el.hasAttribute('data-dock-item') || el.hasAttribute('data-dock-separator'),
    );
  }, []);

  const itemElements = useCallback(
    (): HTMLElement[] => layoutChildren().filter((el) => el.hasAttribute('data-dock-item')),
    [layoutChildren],
  );

  const handleFor = useCallback((el: HTMLElement): DockItemHandle | undefined => {
    const id = el.dataset.dockId;
    return id ? handlesRef.current.get(id) : undefined;
  }, []);

  const visibleItemElements = useCallback((): HTMLElement[] => {
    const dragged = dragRef.current?.active ? dragRef.current.el : null;
    return itemElements().filter((el) => el !== dragged && el.style.display !== 'none');
  }, [itemElements]);

  const setItemScale = useCallback((handle: DockItemHandle, scale: number) => {
    // Skip no-op writes: retargeting a channel to the value it already holds
    // restarts its tween for nothing.
    if (Math.abs(handle.scale() - scale) <= 1e-4) return;
    handle.setScale(scale);
  }, []);

  const resetAll = useCallback(() => {
    const dragged = dragRef.current?.el;
    itemElements().forEach((el) => {
      if (el === dragged || el.style.display === 'none') return;
      const handle = handleFor(el);
      if (handle) setItemScale(handle, 1);
    });
  }, [handleFor, itemElements, setItemScale]);

  /* ------------------------------------------------------------ FLIP helper */

  const applyFlip = useCallback((before: Map<HTMLElement, DOMRect>) => {
    before.forEach((rect, el) => {
      if (!el.isConnected) return;
      const now = el.getBoundingClientRect();
      const dx = rect.left - now.left;
      const dy = rect.top - now.top;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
      el.animate(
        [{transform: `translate(${dx}px, ${dy}px)`}, {transform: 'translate(0px, 0px)'}],
        {duration: REORDER_DURATION, easing: REORDER_EASE},
      );
    });
  }, []);

  /** Measure, mutate synchronously, then animate the delta away. */
  const flipSync = useCallback(
    (mutate: () => void) => {
      const dragged = dragRef.current?.el;
      const children = layoutChildren().filter((el) => el !== dragged);
      const before = new Map(children.map((el) => [el, el.getBoundingClientRect()] as const));
      mutate();
      applyFlip(before);
    },
    [applyFlip, layoutChildren],
  );

  // React-driven reorders commit asynchronously, so the "before" rects are
  // stashed and consumed on the very next layout pass.
  useLayoutEffect(() => {
    const before = pendingFlipRef.current;
    if (!before) return;
    pendingFlipRef.current = null;
    applyFlip(before);
  });

  /* ------------------------------------------------------- hover / magnify  */

  const applyHoverScale = useCallback(
    (clientX: number, clientY: number) => {
      const cfg = cfgRef.current;
      if (cfg.disabled) return;

      const {x, y} = mousePosRef.current;
      const offset = cfg.direction === 'horizontal' ? clientX - x : clientY - y;
      // Sub-pixel jitter would cost a frame of work for no visible change.
      if (Math.abs(offset) <= 2) return;
      mousePosRef.current = {x: clientX, y: clientY};

      const pointer = cfg.direction === 'horizontal' ? clientX : clientY;
      const items = visibleItemElements();

      // READ phase — measure everything first.
      const rects = items.map((el) => el.getBoundingClientRect());

      // WRITE phase — now it is safe to touch styles.
      items.forEach((el, index) => {
        const handle = handleFor(el);
        const rect = rects[index];
        if (!handle || !rect) return;
        const center =
          cfg.direction === 'horizontal'
            ? rect.left + rect.width / 2
            : rect.top + rect.height / 2;
        const scale = calculateHoverScale(
          pointer,
          center,
          handle.baseSize(cfg.direction),
          cfg.maxRange,
          cfg.maxScale,
        );
        setItemScale(handle, scale);
      });
    },
    [handleFor, setItemScale, visibleItemElements],
  );

  const cancelHoverFrame = useCallback(() => {
    if (hoverFrameRef.current !== null) cancelAnimationFrame(hoverFrameRef.current);
    hoverFrameRef.current = null;
    pendingHoverRef.current = null;
  }, []);

  const queueHoverScale = useCallback(
    (clientX: number, clientY: number) => {
      if (cfgRef.current.disabled) return;
      // Coalesce: many mousemove events inside one frame collapse into a single
      // update and the LAST pointer position wins.
      pendingHoverRef.current = {x: clientX, y: clientY};
      if (hoverFrameRef.current !== null) return;
      hoverFrameRef.current = requestAnimationFrame(() => {
        hoverFrameRef.current = null;
        const move = pendingHoverRef.current;
        pendingHoverRef.current = null;
        if (move) applyHoverScale(move.x, move.y);
      });
    },
    [applyHoverScale],
  );

  /* --------------------------------------------------------------- sorting  */

  const computeBlock = useCallback(
    (el: HTMLElement): DockBlock => {
      const cfg = cfgRef.current;
      const ul = containerRef.current;
      const children = layoutChildren();
      const index = children.indexOf(el);
      if (!ul || index < 0) return {ids: [], items: [], max: 0, min: 0};

      let start = index;
      let end = index;
      while (start > 0 && !children[start - 1]!.hasAttribute('data-dock-separator')) start -= 1;
      while (end < children.length - 1 && !children[end + 1]!.hasAttribute('data-dock-separator'))
        end += 1;

      const items = children
        .slice(start, end + 1)
        .filter((child) => child.hasAttribute('data-dock-item'));
      const prevSeparator = start > 0 ? children[start - 1]! : null;
      const nextSeparator = end < children.length - 1 ? children[end + 1]! : null;
      const dockRect = ul.getBoundingClientRect();
      const horizontal = cfg.direction === 'horizontal';

      const min = prevSeparator
        ? horizontal
          ? prevSeparator.getBoundingClientRect().right
          : prevSeparator.getBoundingClientRect().bottom
        : horizontal
          ? dockRect.left
          : dockRect.top;
      const max = nextSeparator
        ? horizontal
          ? nextSeparator.getBoundingClientRect().left
          : nextSeparator.getBoundingClientRect().top
        : horizontal
          ? dockRect.right
          : dockRect.bottom;

      return {ids: items.map((item) => item.dataset.dockId!), items, max, min};
    },
    [layoutChildren],
  );

  const updatePreview = useCallback((drag: DragState, clientX: number, clientY: number) => {
    const x = clientX - drag.offset.fx * drag.previewSize.width;
    const y = clientY - drag.offset.fy * drag.previewSize.height;
    drag.translate = {x, y};
    if (!drag.preview) return;
    // translate3d + scale keeps the preview on the compositor; writing left/top
    // every frame would relayout the whole document instead.
    drag.preview.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${DRAG_SCALE})`;
  }, []);

  const createPreview = useCallback((drag: DragState) => {
    const preview = document.createElement('div');
    preview.className = 'dock-drag-preview';
    preview.style.cssText = [
      'position: fixed',
      'left: 0',
      'top: 0',
      `width: ${drag.previewSize.width}px`,
      `height: ${drag.previewSize.height}px`,
      'pointer-events: none',
      'z-index: 2147482000',
      'will-change: transform',
      'transform-origin: center center',
      `transform: translate3d(${drag.translate.x}px, ${drag.translate.y}px, 0) scale(1)`,
    ].join(';');

    const clone = drag.el.cloneNode(true) as HTMLElement;
    clone.removeAttribute('data-dock-item');
    clone.removeAttribute('data-dock-id');
    clone.style.width = `${drag.previewSize.width}px`;
    clone.style.height = `${drag.previewSize.height}px`;
    clone.style.opacity = '1';
    clone.querySelectorAll('.dock-tooltip').forEach((node) => node.remove());
    const scaleEl = clone.querySelector<HTMLElement>('.dock-item__scale');
    if (scaleEl) {
      scaleEl.style.width = `${drag.previewSize.width}px`;
      scaleEl.style.height = `${drag.previewSize.height}px`;
      scaleEl.style.transform = 'scale(1)';
    }
    preview.appendChild(clone);
    document.body.appendChild(preview);
    return preview;
  }, []);

  const computeTargetIndex = useCallback(
    (drag: DragState, block: DockBlock, clientX: number, clientY: number) => {
      const cfg = cfgRef.current;
      const horizontal = cfg.direction === 'horizontal';
      const pointer = horizontal ? clientX : clientY;
      const allIds = itemElements().map((el) => el.dataset.dockId!);
      const withoutDragged = allIds.filter((id) => id !== drag.id);
      const blockVisible = block.items.filter(
        (el) => el !== drag.el && el.style.display !== 'none' && el.isConnected,
      );
      if (blockVisible.length === 0) return -1;

      let local = blockVisible.length;
      for (let index = 0; index < blockVisible.length; index += 1) {
        const rect = blockVisible[index]!.getBoundingClientRect();
        const center = horizontal ? rect.left + rect.width / 2 : rect.top + rect.height / 2;
        if (pointer < center) {
          local = index;
          break;
        }
      }

      if (local < blockVisible.length) {
        return withoutDragged.indexOf(blockVisible[local]!.dataset.dockId!);
      }
      return withoutDragged.indexOf(blockVisible[blockVisible.length - 1]!.dataset.dockId!) + 1;
    },
    [itemElements],
  );

  const hideDraggedFootprint = useCallback(
    (drag: DragState, hidden: boolean) => {
      const alreadyHidden = drag.el.style.display === 'none';
      if (alreadyHidden === hidden) return;
      flipSync(() => {
        drag.el.style.display = hidden ? 'none' : '';
      });
    },
    [flipSync],
  );

  const processDragMove = useCallback(
    (clientX: number, clientY: number) => {
      const drag = dragRef.current;
      const ul = containerRef.current;
      if (!drag?.active || !ul) return;

      const cfg = cfgRef.current;
      // Blocks are recomputed each frame: a reorder changes both the DOM order
      // inside the block and the separator positions that bound it.
      const block = computeBlock(drag.el);
      drag.block = block;

      const dockRect = ul.getBoundingClientRect();
      const insideDock = isPointInsideRect(dockRect, clientX, clientY);
      const axisValue = cfg.direction === 'horizontal' ? clientX : clientY;
      const insideBlock = axisValue >= block.min && axisValue <= block.max;

      if (!insideDock && cfg.allowDragDelete) {
        // Leaving the dock stages a removal: the footprint collapses so the
        // remaining icons close the gap, previewing what the drop will do.
        if (!drag.outside) {
          drag.outside = true;
          drag.preview?.classList.add('is-removing');
          hideDraggedFootprint(drag, true);
          resetAll();
        }
        updatePreview(drag, clientX, clientY);
        return;
      }

      if (drag.outside) {
        drag.outside = false;
        drag.preview?.classList.remove('is-removing');
      }

      if (!insideDock || !insideBlock) {
        // Crossing a separator is an invalid drop: collapse the footprint and
        // wait. Releasing here snaps the item back where it came from.
        hideDraggedFootprint(drag, true);
        updatePreview(drag, clientX, clientY);
        resetAll();
        return;
      }

      hideDraggedFootprint(drag, false);

      const targetIndex = computeTargetIndex(drag, block, clientX, clientY);
      if (targetIndex >= 0 && targetIndex !== drag.currentIndex) {
        const allIds = itemElements().map((el) => el.dataset.dockId!);
        const nextIds = moveItem(allIds, drag.id, targetIndex);
        const children = layoutChildren().filter((el) => el !== drag.el);
        pendingFlipRef.current = new Map(
          children.map((el) => [el, el.getBoundingClientRect()] as const),
        );
        drag.currentIndex = targetIndex;
        callbacksRef.current.onSort?.({
          fromIndex: drag.fromIndex,
          id: drag.id,
          ids: nextIds,
          toIndex: targetIndex,
        });
      }

      updatePreview(drag, clientX, clientY);
      applyHoverScale(clientX, clientY);
    },
    [
      applyHoverScale,
      computeBlock,
      computeTargetIndex,
      hideDraggedFootprint,
      itemElements,
      layoutChildren,
      resetAll,
      updatePreview,
    ],
  );

  const cancelDragFrame = useCallback(() => {
    if (dragFrameRef.current !== null) cancelAnimationFrame(dragFrameRef.current);
    dragFrameRef.current = null;
    pendingDragRef.current = null;
  }, []);

  const queueDragMove = useCallback(
    (clientX: number, clientY: number) => {
      pendingDragRef.current = {x: clientX, y: clientY};
      if (dragFrameRef.current !== null) return;
      dragFrameRef.current = requestAnimationFrame(() => {
        dragFrameRef.current = null;
        const move = pendingDragRef.current;
        pendingDragRef.current = null;
        if (move) processDragMove(move.x, move.y);
      });
    },
    [processDragMove],
  );

  const cleanupDrag = useCallback(() => {
    cancelDragFrame();
    containerRef.current?.classList.remove('dock--sorting');
    dragRef.current = null;
  }, [cancelDragFrame]);

  const beginDrag = useCallback(
    (drag: DragState) => {
      drag.active = true;
      suppressClickRef.current = true;
      containerRef.current?.classList.add('dock--sorting');

      // Sorting is not magnifying: settle every icon so the floating preview
      // matches the slot it will drop into.
      resetAll();

      drag.preview = createPreview(drag);
      drag.el.style.opacity = '0';
      drag.el.style.pointerEvents = 'none';
      drag.preview.animate(
        [
          {transform: `translate3d(${drag.translate.x}px, ${drag.translate.y}px, 0) scale(1)`},
          {
            transform: `translate3d(${drag.translate.x}px, ${drag.translate.y}px, 0) scale(${DRAG_SCALE})`,
          },
        ],
        {duration: 140, easing: REORDER_EASE},
      );
    },
    [createPreview, resetAll],
  );

  const finishDrag = useCallback(
    (drag: DragState) => {
      const preview = drag.preview;
      const cfg = cfgRef.current;
      const el = drag.el;

      if (!preview) {
        el.style.removeProperty('display');
        el.style.removeProperty('opacity');
        el.style.removeProperty('pointer-events');
        cleanupDrag();
        return;
      }

      if (drag.outside && cfg.allowDragDelete) {
        const index = drag.fromIndex;
        preview
          .animate(
            [
              {
                opacity: 1,
                transform: `translate3d(${drag.translate.x}px, ${drag.translate.y}px, 0) scale(${DRAG_SCALE})`,
              },
              {
                opacity: 0,
                transform: `translate3d(${drag.translate.x}px, ${drag.translate.y}px, 0) scale(1.9)`,
              },
            ],
            {duration: 280, easing: 'cubic-bezier(0.4, 0, 1, 1)'},
          )
          .finished.catch(() => undefined)
          .finally(() => preview.remove());

        el.style.removeProperty('display');
        el.style.removeProperty('opacity');
        el.style.removeProperty('pointer-events');
        callbacksRef.current.onDelete?.({id: drag.id, index});
        cleanupDrag();
        return;
      }

      // Snap the floating preview onto the slot the item now occupies, then hand
      // the pixels back to the real element.
      el.style.removeProperty('display');
      const targetRect = el.getBoundingClientRect();
      const targetX = targetRect.left + (targetRect.width - drag.previewSize.width) / 2;
      const targetY = targetRect.top + (targetRect.height - drag.previewSize.height) / 2;

      preview
        .animate(
          [
            {
              transform: `translate3d(${drag.translate.x}px, ${drag.translate.y}px, 0) scale(${DRAG_SCALE})`,
            },
            {transform: `translate3d(${targetX}px, ${targetY}px, 0) scale(1)`},
          ],
          {duration: REORDER_DURATION, easing: SNAP_BACK_EASE, fill: 'forwards'},
        )
        .finished.catch(() => undefined)
        .finally(() => {
          preview.remove();
          el.style.removeProperty('opacity');
          el.style.removeProperty('pointer-events');
        });

      cleanupDrag();
    },
    [cleanupDrag],
  );

  /* ------------------------------------------------------------- listeners  */

  useEffect(() => {
    const ul = containerRef.current;
    if (!ul) return;

    const onMouseMove = (event: MouseEvent) => {
      if (dragRef.current?.active) return;
      queueHoverScale(event.clientX, event.clientY);
    };

    const onMouseLeave = () => {
      if (dragRef.current?.active) return;
      cancelHoverFrame();
      mousePosRef.current = {x: 0, y: 0};
      resetAll();
    };

    const onClickCapture = (event: Event) => {
      if (!suppressClickRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = false;
    };

    const onDragStart = (event: DragEvent) => {
      // Native HTML5 drag would fight the pointer-based sort.
      if (cfgRef.current.sortable) event.preventDefault();
    };

    const onPointerDown = (event: PointerEvent) => {
      const cfg = cfgRef.current;
      if (dragRef.current || !cfg.sortable || cfg.disabled || event.button !== 0) return;

      const target = event.target as Element | null;
      const el = target?.closest<HTMLElement>('[data-dock-item]');
      if (!el || !ul.contains(el) || !el.dataset.dockId) return;

      const rect = el.getBoundingClientRect();
      const handle = handleFor(el);
      const width = handle?.baseSize('horizontal') ?? rect.width;
      const height = handle?.baseSize('vertical') ?? rect.height;
      const index = itemElements().indexOf(el);

      dragRef.current = {
        active: false,
        block: computeBlock(el),
        currentIndex: index,
        el,
        fromIndex: index,
        id: el.dataset.dockId,
        offset: {
          fx: rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0.5,
          fy: rect.height > 0 ? (event.clientY - rect.top) / rect.height : 0.5,
        },
        outside: false,
        pointerId: event.pointerId,
        preview: null,
        previewSize: {height, width},
        startX: event.clientX,
        startY: event.clientY,
        translate: {x: rect.left, y: rect.top},
      };
    };

    const onWindowPointerMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;

      if (!drag.active) {
        const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
        if (distance < DRAG_START_DISTANCE) return;
        updatePreview(drag, event.clientX, event.clientY);
        beginDrag(drag);
      }

      event.preventDefault();
      queueDragMove(event.clientX, event.clientY);
    };

    const onWindowPointerUp = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      cancelDragFrame();
      if (!drag.active) {
        dragRef.current = null;
        return;
      }
      finishDrag(drag);
      // Let the suppressed click flush before hover takes over again.
      requestAnimationFrame(() => {
        suppressClickRef.current = false;
        mousePosRef.current = {x: 0, y: 0};
      });
    };

    ul.addEventListener('mousemove', onMouseMove);
    ul.addEventListener('mouseleave', onMouseLeave);
    ul.addEventListener('click', onClickCapture, true);
    ul.addEventListener('dragstart', onDragStart);
    ul.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onWindowPointerMove, {passive: false});
    window.addEventListener('pointerup', onWindowPointerUp);
    window.addEventListener('pointercancel', onWindowPointerUp);

    return () => {
      ul.removeEventListener('mousemove', onMouseMove);
      ul.removeEventListener('mouseleave', onMouseLeave);
      ul.removeEventListener('click', onClickCapture, true);
      ul.removeEventListener('dragstart', onDragStart);
      ul.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onWindowPointerMove);
      window.removeEventListener('pointerup', onWindowPointerUp);
      window.removeEventListener('pointercancel', onWindowPointerUp);
      cancelHoverFrame();
      cancelDragFrame();
    };
  }, [
    beginDrag,
    cancelDragFrame,
    cancelHoverFrame,
    computeBlock,
    finishDrag,
    handleFor,
    itemElements,
    queueDragMove,
    queueHoverScale,
    resetAll,
    updatePreview,
  ]);

  // Changing axis mid-hover would measure distances against the wrong
  // coordinate, so drop any queued frame and settle.
  useEffect(() => {
    cancelHoverFrame();
    mousePosRef.current = {x: 0, y: 0};
    resetAll();
  }, [cancelHoverFrame, config.direction, resetAll]);

  useEffect(() => {
    if (config.disabled) {
      cancelHoverFrame();
      resetAll();
    }
  }, [cancelHoverFrame, config.disabled, resetAll]);

  const contextValue = useMemo<DockContextValue>(
    () => ({...config, register}),
    [config, register],
  );

  const thicknessKey = config.direction === 'horizontal' ? 'height' : 'width';

  return (
    <DockContext.Provider value={contextValue}>
      <ul
        ref={containerRef}
        className={`dock dock--${config.direction} dock--${config.position} ${className ?? ''}`}
        style={{
          ...style,
          // The shell keeps a constant thickness; only the items inflate.
          [thicknessKey]: `${config.padding * 2 + config.size}px`,
          gap: `${config.gap}px`,
          padding: `${config.padding}px`,
        }}
      >
        {children}
      </ul>
    </DockContext.Provider>
  );
}
